// @ts-nocheck

import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import styled, { css, keyframes } from 'styled-components';

import { Target } from '../../client';
import { getPortalRootDOMElement } from '../../DOMElements';
import { ZIndices } from '../../styled-components/dark/common';
import ExitButton from '../ExitButton';

const APPEARANCE_TRANSITION_DURATION = 200; // ms

const appearAnim = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const Wrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;

  ${({ $forbidBackgroundScrolling }) =>
    $forbidBackgroundScrolling
      ? css`
          pointer-events: auto;
        `
      : css`
          pointer-events: none;
        `}

  ${({ $classNames, $zIndex }) => css`
    z-index: ${$zIndex};

    &.${$classNames}-enter {
      animation: ${appearAnim} ${APPEARANCE_TRANSITION_DURATION}ms ease-out forwards;
    }

    &.${$classNames}-exit {
      animation: ${appearAnim} ${APPEARANCE_TRANSITION_DURATION}ms ease-out forwards reverse;
    }
  `}
`;

const StyleReceiver = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: auto;

  ${({ $hideCloseButton }) =>
    $hideCloseButton
      ? css`
          ${Target.__MOBILE__
            ? css`
                width: calc(100% - 2em);
              `
            : css`
                width: 40em;
              `}
        `
      : css`
          ${Target.__MOBILE__
            ? css`
                width: calc(100% - 4em);
              `
            : css`
                width: 40em;
              `}
        `}
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  background-color: black;
  opacity: 0.7;

  pointer-events: auto;

  z-index: -1;
`;

const CloseButton = styled(ExitButton)`
  --ExitButton-right: var(--AdaptativeModalCloseButton-right, 0);
  --ExitButton-top: var(--AdaptativeModalCloseButton-top, 0);
`;

type AdaptativeModalProps = {
  className?: string;
  show?: boolean;
  rootElement?: unknown;
  zIndex?: number;
  identifier?: string;
  onClose?: () => void;
  hideOverlay?: boolean;
  hideCloseButton?: boolean;
  forbidBackgroundScrolling?: boolean;
  children: React.ReactNode;
};

const AdaptativeModal = ({
  className = '',
  show = false,
  rootElement = getPortalRootDOMElement(),
  zIndex = ZIndices.modal,
  identifier,
  onClose,
  hideOverlay = false,
  hideCloseButton = false,
  forbidBackgroundScrolling = false,
  children,
}: AdaptativeModalProps) => {
  // eslint-disable-next-line sonarjs/no-nested-template-literals
  const classNames = useMemo(() => `adaptative-modal${identifier ? `-${identifier}` : ''}-transitions`, [identifier]);

  useEffect(() => {
    if (forbidBackgroundScrolling) {
      const bodyStyle = document.body?.style;

      if (bodyStyle) {
        if (show) {
          bodyStyle.overflow = 'hidden';
        } else {
          bodyStyle.overflow = '';
        }
      }
    }
  }, [forbidBackgroundScrolling, show]);

  return createPortal(
    <CSSTransition
      classNames={classNames}
      in={show}
      timeout={APPEARANCE_TRANSITION_DURATION}
      mountOnEnter
      unmountOnExit
    >
      <Wrapper $classNames={classNames} $zIndex={zIndex} $forbidBackgroundScrolling={forbidBackgroundScrolling}>
        <StyleReceiver className={className} $hideCloseButton={hideCloseButton}>
          {children}
          {!hideCloseButton ? <CloseButton onClick={onClose} /> : null}
        </StyleReceiver>
        {!hideOverlay ? <Overlay onClick={onClose} /> : null}
      </Wrapper>
    </CSSTransition>,
    rootElement,
  );
};

export default AdaptativeModal;
